import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { TiArrowUnsorted } from "react-icons/ti";
import { useTable, useGlobalFilter, useSortBy, usePagination } from "react-table";
import * as XLSX from "xlsx";
import * as actions from "../../../Redux/Actions";
import MainCard from "../../../component/MainCard/MainCard";
import SearchBar from "../../../component/SearchBar/SearchBar";
import ModalComponents from "../../../component/ModalComponent/ModalComponent";
import ShowEntries from "../../../component/ShowEntries/ShowEntries";
import TablePagination from "../../../component/TablePagination/TablePagination";
import hanldeUnauthorizedUser from "../../../component/AccessConditions/UnauthorizedUser";
import useLoadingIndicator from "../../../component/Loader/Loader";
import SetWeightage from "./SetWeightage";
import ImportWeightageModal from "./ImportWeightageModal";
import "../../SuccessionPlanning/readable-form.scss";

const DesignationsWeightage = () => {
  const { unAuthorizeUser } = hanldeUnauthorizedUser();
  const dispatch = useDispatch();

  const [designations, setDesignations] = useState<any[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | configured | notset
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [showImport, setShowImport] = useState(false);

  const { showLoadingIndicator, hideLoadingIndicator, loadingComponent } =
    useLoadingIndicator();

  const loadDesignations = () => {
    showLoadingIndicator();
    dispatch(
      actions.getDesignationsWithStatsAction((res: any) => {
        if (res?.data) setDesignations(res.data);
        hideLoadingIndicator();
      })
    );
  };

  useEffect(() => {
    unAuthorizeUser();
    loadDesignations();
  }, []);

  const openWeightage = (row: any) => {
    setSelected(row);
    setShowModal(true);
  };

  // Client-side template so it never drifts from the import's expected columns.
  const downloadTemplate = () => {
    // Header-only: no sample rows, so nothing gets accidentally imported. The
    // expected format is explained in the import modal instead.
    const aoa = [["Designation", "KRA", "Weightage"]];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Weightage");
    XLSX.writeFile(wb, "Designation Weightage Template.xlsx");
  };

  const isConfigured = (d: any) => d.kraCount > 0 && Math.abs((d.totalWeight || 0) - 100) < 0.01;

  const term = searchValue.toLowerCase();
  const filtered = useMemo(
    () =>
      designations.filter((d) => {
        if (!(d.title || "").toLowerCase().includes(term)) return false;
        if (statusFilter === "configured") return d.kraCount > 0;
        if (statusFilter === "notset") return !d.kraCount;
        return true;
      }),
    [designations, term, statusFilter]
  );

  const columns: any = useMemo(
    () => [
      { Header: "Designation", accessor: "title" },
      { Header: "Employees", accessor: "employeeCount" },
      {
        Header: "KRAs & Weightage",
        accessor: "kraCount",
        Cell: (row: any) => {
          const d = row.row.original;
          if (!d.kraCount) return <span style={{ color: "#dc3545" }}>Not set</span>;
          const ok = isConfigured(d);
          return (
            <span style={{ color: ok ? "#198754" : "#fd7e14" }}>
              {d.kraCount} KRA{d.kraCount > 1 ? "s" : ""} &middot; {d.totalWeight}%
              {ok ? "" : " (incomplete)"}
            </span>
          );
        },
      },
      {
        Header: "Action",
        accessor: "action",
        Cell: (row: any) => (
          <button
            className="btn add-submit-button buttonsize"
            onClick={() => openWeightage(row.row.original)}
          >
            {row.row.original.kraCount ? "Edit Weightage" : "Set Weightage"}
          </button>
        ),
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    setPageSize,
    pageCount,
    gotoPage,
    state: { pageSize, pageIndex },
  } = useTable(
    // API already returns employeeCount desc; headers allow re-sorting
    { columns, data: filtered, initialState: { pageSize: 25, pageIndex: 0 } as any },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const notSetCount = designations.filter((d) => !d.kraCount).length;

  return (
    <>
      {loadingComponent()}
      <MainCard>
      <div className="readable-table">
      <div className="assessment-heading-button-wrapper">
        <div>
          <h2>Designations &amp; Weightage</h2>
          <p style={{ color: "#6c757d" }}>
            {designations.length} designations &middot; {notSetCount} still need KRAs
          </p>
        </div>
        <div className="btn-wrapper" style={{ display: "flex", gap: 12 }}>
          <button className="btn cancel-btn buttonsize" onClick={downloadTemplate}>
            Download Template
          </button>
          <button className="btn add-submit-button buttonsize" onClick={() => setShowImport(true)}>
            Import
          </button>
        </div>
      </div>

      <div className="mt-2">
        <div className="mt-3 buttonsize filter-assessment-wrapper">
          <div className="assessment-searchbar-wrapper mx-2">
            <SearchBar filter={searchValue} setFilter={setSearchValue} onSearchClicked={() => {}} />
          </div>
          <div className="assessment-filter-container mx-2">
            <select
              id="select-filters"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="notset">Not set</option>
              <option value="configured">Configured</option>
            </select>
          </div>
        </div>

        {filtered.length > 0 ? (
          <>
            <div className="table table-responsive">
              <table className="table-main table table-sm mt-3" {...getTableProps()}>
                <thead className="table-head">
                  {headerGroups.map((headerGroup) => (
                    <tr className="table-head-row" {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column: any) => (
                        <th
                          className="table-head-row"
                          {...column.getHeaderProps(column.getSortByToggleProps())}
                          scope="col"
                        >
                          {column.render("Header")}
                          <span>{column.id !== "action" ? <TiArrowUnsorted /> : ""}</span>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="table-body" {...getTableBodyProps()}>
                  {page.map((row: any) => {
                    prepareRow(row);
                    return (
                      <tr className="table-body-row" {...row.getRowProps()}>
                        {row.cells.map((cell: any) => (
                          <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="show-entries-wrapper">
              <ShowEntries
                pageSize={pageSize}
                setPageSize={setPageSize}
                totalRecords={filtered.length}
                totalEntries={filtered.length}
              />
              {pageCount > 1 && (
                <TablePagination
                  totalPages={pageCount}
                  currentPage={pageIndex + 1}
                  onClick={(p: number) => gotoPage(p - 1)}
                />
              )}
            </div>
          </>
        ) : (
          <p className="employeedata mt-2">No designations found</p>
        )}
      </div>

      <ModalComponents
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setSelected(null);
          loadDesignations();
        }}
        title={selected ? `Weightage — ${selected.title}` : "Weightage"}
        modalBody={
          selected ? (
            <SetWeightage
              designation={selected}
              setShowModal={setShowModal}
              setSelected={setSelected}
            />
          ) : null
        }
        className="modal-lg"
      />

      <ModalComponents
        show={showImport}
        onHide={() => setShowImport(false)}
        title="Import Designation Weightage"
        modalBody={
          <ImportWeightageModal
            onHide={() => setShowImport(false)}
            onImported={loadDesignations}
          />
        }
        className="modal-lg"
      />
      </div>
      </MainCard>
    </>
  );
};

export default DesignationsWeightage;
